import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Flame, Mail, Clock3, ArrowLeft, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import CosmicBackground from '../components/CosmicBackground';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const AnimatedScoreBar = ({
  icon,
  label,
  value,
  colorClass = 'from-pink-500 to-fuchsia-500',
}) => {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium text-white">{label}</span>
        </div>
        <span className="text-sm font-semibold text-white">{safeValue}%</span>
      </div>

      <div className="h-3 w-full overflow-hidden rounded-full bg-white/10">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${safeValue}%` }}
          transition={{ duration: 1.1, ease: 'easeOut' }}
          className={`h-full rounded-full bg-gradient-to-r ${colorClass}`}
        />
      </div>
    </div>
  );
};

const LovePortal = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const readingOptions = [
    {
      key: 'soulmate',
      title: t('loveSoulmateTitle'),
      description: t('loveSoulmateDescription'),
      icon: Heart,
      accent: 'from-pink-500 to-rose-500',
      border: 'border-pink-500/30',
      text: 'text-pink-300',
    },
    {
      key: 'twin_flame',
      title: t('loveTwinFlameTitle'),
      description: t('loveTwinFlameDescription'),
      icon: Flame,
      accent: 'from-fuchsia-500 to-violet-500',
      border: 'border-fuchsia-500/30',
      text: 'text-fuchsia-300',
    },
    {
      key: 'message_from_them',
      title: t('loveMessageFromThemTitle'),
      description: t('loveMessageFromThemDescription'),
      icon: Mail,
      accent: 'from-cyan-500 to-sky-500',
      border: 'border-cyan-500/30',
      text: 'text-cyan-300',
    },
    {
      key: 'love_timing',
      title: t('loveTimingTitle'),
      description: t('loveTimingDescription'),
      icon: Clock3,
      accent: 'from-amber-500 to-orange-500',
      border: 'border-amber-500/30',
      text: 'text-amber-300',
    },
  ];

  const [selectedType, setSelectedType] = useState('soulmate');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [formData, setFormData] = useState({
    your_name: '',
    your_birth_date: '',
    their_name: '',
    their_birth_date: '',
    relationship_context: '',
    question: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRaw = localStorage.getItem('user');

    if (!token || !userRaw) {
      navigate('/login');
      return;
    }

    try {
      const user = JSON.parse(userRaw);
      setFormData((prev) => ({
        ...prev,
        your_name: user?.full_name || '',
      }));
    } catch (error) {
      console.error('Failed to parse user from localStorage', error);
    }

    fetchHistory();
  }, [navigate, language]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/universe/love-portal-history?language=${language}`);
      setHistory(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch Love Portal history', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.question.trim() && !formData.relationship_context.trim()) {
      toast.error(t('loveQuestionOrContextRequired'));
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post('/universe/love-portal', {
        reading_type: selectedType,
        language,
        your_name: formData.your_name,
        your_birth_date: formData.your_birth_date,
        their_name: formData.their_name,
        their_birth_date: formData.their_birth_date,
        relationship_context: formData.relationship_context,
        question: formData.question,
      });

      setResult(response?.data || null);
      await fetchHistory();

      setTimeout(() => {
        document.getElementById('love-result-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t('lovePortalFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedConfig =
    readingOptions.find((option) => option.key === selectedType) || readingOptions[0];

  const SelectedIcon = selectedConfig.icon;

  const scoreCards = useMemo(() => {
    if (!result) return [];

    return [
      {
        icon: '❤️',
        label: t('loveCompatibility'),
        value: result.compatibility_score,
        colorClass: 'from-pink-500 to-rose-500',
      },
      {
        icon: '🔥',
        label: t('lovePassion'),
        value: result.passion_score,
        colorClass: 'from-orange-500 to-amber-500',
      },
      {
        icon: '🧠',
        label: t('loveCommunication'),
        value: result.communication_score,
        colorClass: 'from-sky-500 to-cyan-500',
      },
      {
        icon: '⏳',
        label: t('loveTimingScore'),
        value: result.timing_score,
        colorClass: 'from-violet-500 to-fuchsia-500',
      },
    ];
  }, [result, t]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <CosmicBackground />

      <div
        className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pink-900/20 via-slate-900 to-slate-950 pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10">
       <header
         className="px-6 sm:px-12 border-b border-white/5"
         style={{
           paddingTop: '70px',
           paddingBottom: '24px'
         }}
        >
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={18} />
                <span className="text-sm">{t('back')}</span>
              </button>
              <Logo size="md" />
            </div>

            <LanguageSelector />
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 text-center border border-pink-500/20"
          >
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-600 flex items-center justify-center shadow-[0_0_35px_rgba(236,72,153,0.35)]">
                <Heart className="w-8 h-8 text-white" />
              </div>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3">
              {t('lovePortal')}
            </h1>

            <p className="text-slate-300 max-w-2xl mx-auto text-lg leading-relaxed">
              {t('lovePortalSubtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="grid md:grid-cols-2 xl:grid-cols-4 gap-4"
          >
            {readingOptions.map((option) => {
              const Icon = option.icon;
              const isActive = selectedType === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSelectedType(option.key)}
                  className={`glass-card p-5 text-left transition-all ${
                    isActive
                      ? `${option.border} ring-2 ring-white/10 scale-[1.02]`
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-r ${option.accent} flex items-center justify-center mb-4`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className={`font-semibold mb-2 ${isActive ? option.text : 'text-white'}`}>
                    {option.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{option.description}</p>
                </button>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className={`w-12 h-12 rounded-full bg-gradient-to-r ${selectedConfig.accent} flex items-center justify-center`}
              >
                <SelectedIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedConfig.title}</h2>
                <p className="text-slate-400">{selectedConfig.description}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('loveYourName')}</label>
                  <input
                    type="text"
                    value={formData.your_name}
                    onChange={(e) => setFormData({ ...formData, your_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                    placeholder={t('loveYourNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('loveYourBirthDate')}</label>
                  <input
                    type="date"
                    value={formData.your_birth_date}
                    onChange={(e) => setFormData({ ...formData, your_birth_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('loveTheirName')}</label>
                  <input
                    type="text"
                    value={formData.their_name}
                    onChange={(e) => setFormData({ ...formData, their_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                    placeholder={t('loveTheirNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-300 mb-2">{t('loveTheirBirthDate')}</label>
                  <input
                    type="date"
                    value={formData.their_birth_date}
                    onChange={(e) => setFormData({ ...formData, their_birth_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">{t('loveRelationshipContext')}</label>
                <textarea
                  value={formData.relationship_context}
                  onChange={(e) => setFormData({ ...formData, relationship_context: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40 min-h-[100px] resize-none"
                  placeholder={t('loveRelationshipContextPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">{t('loveYourQuestion')}</label>
                <textarea
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40 min-h-[120px] resize-none"
                  placeholder={t('loveQuestionPlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-600 text-white font-semibold hover:opacity-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-[0_0_40px_rgba(236,72,153,0.25)]"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    <span>{t('lovePortalLoading')}</span>
                  </>
                ) : (
                  <>
                    <Heart size={18} />
                    <span>{t('enterLovePortal')}</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          <AnimatePresence>
            {result && (
              <motion.div
                id="love-result-section"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                className="glass-card p-8 border border-pink-500/25"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{result.title}</h2>
                    <p className="text-slate-400 text-sm">
                      {new Date(result.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {scoreCards.length > 0 && (
                  <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {scoreCards.map((score) => (
                      <AnimatedScoreBar
                        key={score.label}
                        icon={score.icon}
                        label={score.label}
                        value={score.value}
                        colorClass={score.colorClass}
                      />
                    ))}
                  </div>
                )}

                <div className="space-y-5">
                  <div className="p-5 rounded-2xl bg-pink-500/10 border border-pink-500/20">
                    <h3 className="text-pink-300 font-semibold mb-2">{t('loveMainMessage')}</h3>
                    <p className="text-slate-200 leading-relaxed">{result.message}</p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/10">
                      <h3 className="text-white font-semibold mb-2">{t('loveEnergy')}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{result.energy}</p>
                    </div>

                    <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/10">
                      <h3 className="text-white font-semibold mb-2">{t('loveObstacle')}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{result.obstacle}</p>
                    </div>

                    <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/10">
                      <h3 className="text-white font-semibold mb-2">{t('loveNextStep')}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed">{result.next_step}</p>
                    </div>
                  </div>

                  <div className="p-5 rounded-2xl bg-violet-500/10 border border-violet-500/20">
                    <h3 className="text-violet-300 font-semibold mb-2">{t('loveGuidance')}</h3>
                    <p className="text-slate-200 leading-relaxed">{result.guidance}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{t('loveHistory')}</h2>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-pink-400 hover:text-pink-300 transition-colors text-sm font-medium"
              >
                {showHistory ? t('hideHistory') : t('viewHistory')}
              </button>
            </div>

            {historyLoading && (
              <p className="text-slate-400 text-sm">{t('loveLoadingHistory')}</p>
            )}

            <AnimatePresence>
              {showHistory && history.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {history.map((item, idx) => (
                    <motion.div
                      key={item.id || idx}
                      initial={{ opacity: 0, x: -14 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      className="p-5 rounded-2xl bg-slate-900/45 border border-white/10"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <h3 className="text-white font-semibold">{item.title}</h3>
                          <p className="text-pink-300 text-xs uppercase tracking-wide">
                            {String(item.reading_type || '').replaceAll('_', ' ')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-xs">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </div>

                      <p className="text-slate-300 text-sm leading-relaxed mb-3">{item.message}</p>
                      <p className="text-slate-500 text-xs">{item.guidance}</p>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {showHistory && !historyLoading && history.length === 0 && (
              <p className="text-slate-400 text-sm">{t('loveNoHistory')}</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.22 }}
            className="glass-card p-6 border border-amber-500/20"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">{t('lovePremiumTitle')}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {t('lovePremiumDescription')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LovePortal;
